import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import styled from '@emotion/styled';

import { useFlag } from '@innovamat/flags';
import { useUser } from '@innovamat/ga-features';
import {
  TableComponent,
  Typography,
  useColumnsCustomRenderConfig,
  type CustomColumnRenderConfig,
} from '@innovamat/glimmer-components';

import { EllipsisCell } from '../../../../../classrooms-management/components/common-table-components/ellipsis-cell';
import type { TestRow } from '../../../../modules/test/test';
import { TEST_TYPE } from '../../../../modules/test/test';
import { formatDate } from '../../../../utils/date';
import { useTestAndResults } from '../test-and-results-drawer/context/test-and-results-provider';
import { TestAndResultsDrawer } from '../test-and-results-drawer/test-and-results-drawer';
import { TestParticipationCell } from './components/test-participation-cell/test-participation-cell';
import { OpenTestReportCell } from './components/test-report-cell/open-test-report-cell';
import { TestStatusCell } from './components/test-status-cell/test-status-cell';
import { useTestsAndResultsColumnsConfiguration } from './hooks/use-tests-and-results-columns-configuration';

export const Container = styled.div`
  margin-top: 2.5rem;
  margin-bottom: 1rem;
`;

const TextContainer = styled.div`
  flex: 1;
  min-width: 0;
`;

const EllipsableTextWithButton = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;

  .open-drawer-button {
    display: none;
    min-width: 80px;
    white-space: nowrap;
    height: fit-content;
    margin-left: auto;
  }
`;

type Props = {
  rows: TestRow[];
  classroomId: string;
};

const customColumnsRenderConfig = (
  t: TFunction,
  openTestDrawer: (rowId: string) => void,
  classroomId: string
): CustomColumnRenderConfig<TestRow> => ({
  rowName: (_, row) => (
    <EllipsableTextWithButton>
      <TextContainer>
        <Typography.Body1>
          <EllipsisCell
            key={`name_${row.classroomTestRoundId}`}
            text={row.rowName}
          />
        </Typography.Body1>
      </TextContainer>
    </EllipsableTextWithButton>
  ),
  status: (_, row) => (
    <TestStatusCell
      key={`status_${row.classroomTestRoundId}`}
      status={row.status}
    />
  ),
  startDate: (_, row) => formatDate(row.startDate),
  endDate: (_, row) => formatDate(row.endDate),
  report: (_, { classroomTestRoundId, roundName, type, report, testId }) => {
    switch (type) {
      case TEST_TYPE.COSMOS:
      case TEST_TYPE.CONMAT:
      case TEST_TYPE.MOCK:
        return (
          <OpenTestReportCell
            key={`report_${classroomTestRoundId}`}
            testRoundId={classroomTestRoundId}
            onClick={openTestDrawer}
            testId={testId}
            classroomId={classroomId}
          />
        );
      default:
        return <></>;
    }
  },
  participation: (_, { participation, status }) => (
    <TestParticipationCell participation={participation} status={status} />
  ),
});

const TestsAndResultsTable = ({ rows, classroomId }: Props): JSX.Element => {
  const { t } = useTranslation();
  const { user } = useUser();

  const { value: showMockExamsDetailButton } = useFlag(
    'mockExamsDetail',
    false,
    {
      identifier: '',
      email: user?.email ?? '',
      custom: {},
    }
  );

  const { testRowIds, onOpenDrawer } = useTestAndResults();

  const columnsWithoutRender = useTestsAndResultsColumnsConfiguration();
  const columns = useColumnsCustomRenderConfig<TestRow>(
    customColumnsRenderConfig(t, onOpenDrawer, classroomId),
    columnsWithoutRender
  );

  return (
    <Container>
      <TableComponent
        columns={columns}
        id="tests-and-results-table"
        key="tests-and-results-table"
        clickableRows={showMockExamsDetailButton ? testRowIds : []}
        rows={rows}
      />
      <TestAndResultsDrawer key="mock-test-drawer" />
    </Container>
  );
};

export { TestsAndResultsTable };
